import { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { COLUMN_OVERSCAN, getTeamCalGridMetrics } from '../constants/gridLayout';

/**
 * Horizontal virtualization for employee columns inside a scroll container.
 * @param {{ scrollRef: React.RefObject<HTMLElement|null>, columnCount: number, getMetrics?: (viewportWidth: number) => { gutterWidthPx: number, columnWidthPx: number } }} params
 */
export function useHorizontalColumnVirtual({
  scrollRef,
  columnCount,
  getMetrics = getTeamCalGridMetrics,
  stickySidebar = true,
}) {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  const measure = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nextWidth = el.clientWidth;
    setViewportWidth((prev) => (prev === nextWidth ? prev : nextWidth));
  }, [scrollRef]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [scrollRef, measure]);

  const onScroll = useCallback((e) => {
    setScrollLeft(e.currentTarget.scrollLeft);
  }, []);

  const { gutterWidthPx, columnWidthPx } = useMemo(
    () => getMetrics(viewportWidth),
    [viewportWidth, getMetrics]
  );

  const slice = useMemo(() => {
    if (columnCount === 0 || columnWidthPx <= 0 || gutterWidthPx <= 0) {
      return {
        start: 0,
        end: -1,
        leadingSpacerWidth: 0,
        trailingSpacerWidth: 0,
        innerWidth: 0,
        fillViewport: false,
      };
    }

    const minGridWidth = gutterWidthPx + columnCount * columnWidthPx;
    const fillViewport = viewportWidth > 0 && minGridWidth <= viewportWidth;

    if (fillViewport) {
      const innerWidth = Math.max(0, viewportWidth - gutterWidthPx);
      return {
        start: 0,
        end: columnCount - 1,
        leadingSpacerWidth: 0,
        trailingSpacerWidth: 0,
        innerWidth,
        fillViewport: true,
      };
    }

    const innerViewport = Math.max(0, viewportWidth - gutterWidthPx);
    const first = Math.max(
      0,
      Math.floor(scrollLeft / columnWidthPx) - COLUMN_OVERSCAN
    );
    const visibleSlots =
      Math.ceil(innerViewport / columnWidthPx) + 1 + COLUMN_OVERSCAN * 2;
    const last = Math.min(columnCount - 1, first + visibleSlots - 1);

    const innerWidth = columnCount * columnWidthPx;
    const visibleColumnCount = last - first + 1;

    if (stickySidebar) {
      // Sticky gutter + header corner: column strip stays anchored; only the slice changes.
      return {
        start: first,
        end: last,
        leadingSpacerWidth: 0,
        trailingSpacerWidth: innerWidth - visibleColumnCount * columnWidthPx,
        innerWidth,
        fillViewport: false,
      };
    }

    const leadingSpacerWidth = first * columnWidthPx;
    const trailingSpacerWidth = (columnCount - last - 1) * columnWidthPx;

    return {
      start: first,
      end: last,
      leadingSpacerWidth,
      trailingSpacerWidth,
      innerWidth,
      fillViewport: false,
    };
  }, [
    scrollLeft,
    viewportWidth,
    columnCount,
    columnWidthPx,
    gutterWidthPx,
    stickySidebar,
  ]);

  return {
    ...slice,
    onScroll,
    remeasure: measure,
    gutterWidthPx,
    columnWidthPx,
  };
}
